import * as React from 'react'
import { usePopper } from 'react-popper'

import { Box } from './box'
import { Button } from './button'

function getVirtualRef(top: number, left: number) {
  return {
    getBoundingClientRect(): DOMRect {
      const rect = {
        top: top - window.scrollY,
        x: top - window.scrollY,
        left: left - window.scrollX,
        y: left - window.scrollX,
        bottom: window.innerHeight - top,
        right: window.innerWidth - left,
        width: 0,
        height: 0,
      }

      return {
        ...rect,
        toJSON: () => rect,
      }
    },
  }
}

interface ContextMenuOption {
  type?: never
  id: string
  label: React.ReactNode
  onClick: () => void
}

interface ContextMenuDivider {
  type: 'divider'
}

export type ContextMenuItem = ContextMenuOption | ContextMenuDivider

interface ContextMenuProps {
  /**
   * Top coordinate of the click
   */
  top: number

  /**
   * Left coordinate of the click
   */
  left: number

  /**
   * Items visible in the menu
   */
  items: Array<ContextMenuOption | ContextMenuDivider>

  /**
   * Callback to hide the menu
   */
  hide: () => void
}

export function ContextMenu(props: ContextMenuProps) {
  const [popperElement, setPopperElement] = React.useState(null)
  const virtualRef = React.useMemo(() => getVirtualRef(props.top, props.left), [props.left, props.top])
  const { styles, attributes } = usePopper(virtualRef, popperElement, {
    placement: 'right-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [15, 15],
        },
      },
    ],
  })

  return (
    <Box
      // Popper requires to use state for ref
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ref={setPopperElement}
      {...attributes.popper}
      style={styles.popper}
      className="z-dialog flex w-32 flex-col items-stretch space-y-px p-1"
      data-testid="charts-context-menu-body"
    >
      {props.items.map((item, index) => {
        if (item.type === 'divider') {
          return <div key={index} className="mx-1 border-b border-gray-100 dark:border-gray-600" />
        }

        return (
          <Button
            key={index}
            theme="menuItem"
            data-testid={`dropdown-menu-${item.id}`}
            onClick={() => {
              item.onClick()
              props.hide()
            }}
          >
            {item.label}
          </Button>
        )
      })}
    </Box>
  )
}
